import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Autocomplete,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  DialogContent,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material'
import { HighlightOff as HighlightOffIcon } from '@mui/icons-material/'
import {
  BLOOD_GROUPS,
  EMPLOYMENT_TYPES,
  FIELD_TYPES,
  GENDERS,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
} from '../../../utility/constants'
import { adminTeamsService } from '../../../services/admin/teams'
import { listDataService } from '../../../services/listDataService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { toast } from 'react-toastify'
import DateAndDateTime from '../../DataEntryPage/Fields/DateAndDateTime'
import moment from 'moment'
import { formatUserDisplayName } from '../../../utility/userName'

const defaultValue = {
  first_name: '',
  middle_name: '',
  email: '',
  last_name: '',
  is_admin: false,
  company_id: null,
  password: '',
  department: null,
  designation: null,
  sub_department: null,
  primary_contact: null,
  office_contact: null,
  date_of_joining: null,
  date_of_birth: null,
  gender: null,
  permanent_address: null,
  temporary_address: null,
  date_of_leaving: null,
  is_account_active: true,
  blood_group: null,
  aadhar_number: null,
  pan_number: null,
  short_bio: '',
  employment_history: {},
  educational_background: {},
  metadata: null,
  reporting_manager_id: null,
  // New classification fields (list_data_id references)
  category: null,
  user_type: null,
  work_status: null,
  notice_period_end_date: null,
  probation_end_date: null,
}

// Reusable Autocomplete Field Component
const AutocompleteField = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  disabled = false,
  required = false,
}) => (
  <Autocomplete
    options={options}
    getOptionLabel={getOptionLabel}
    value={value}
    onChange={onChange}
    disabled={disabled}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder || label}
        size="small"
        fullWidth
        required={required}
        sx={{ marginTop: 2 }}
      />
    )}
    isOptionEqualToValue={isOptionEqualToValue}
    fullWidth
    size="small"
  />
)

const AddEditUserDialog = ({
  users = [],
  departments = [],
  designations = [],
  designationListData = [],
  departmentListData = [],
  subfunctionListData = [],
  employmentTypeListData = [],
  categoryListData = [],
  userTypeListData = [],
  workStatusListData = [],
  genderListData = [],
  bloodGroupListData = [],
  isOpen,
  setOpenClose,
  fetchFreshList = () => {},
  /** Optional: pre-select a user row when opened from the UserManagement table */
  initialUser = null,
}) => {
  const { user } = useAuthContext()
  const [userDetails, setUserDetails] = useState(defaultValue)
  const [selectedUser, setSelectedUser] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [replacementUser, setReplacementUser] = useState(null)
  const [replacementNeeded, setReplacementNeeded] = useState(null)
  const [showOptionalExternalFields, setShowOptionalExternalFields] = useState(false)

  // When opened from UserManagement table with a pre-selected user, populate the form
  useEffect(() => {
    if (isOpen && initialUser && initialUser.id) {
      setSelectedUser(initialUser)
      setUserDetails({ ...defaultValue, ...initialUser, password: '' })
    } else if (!isOpen) {
      // Reset on close
      setSelectedUser(null)
      setUserDetails(defaultValue)
      setIsSubmitting(false)
    }
  }, [isOpen, initialUser?.id]) // eslint-disable-line

  const yesNoOptions = ['yes', 'no']


  // Memoized options to prevent re-creation on every render
  const employmentTypeOptions = useMemo(
    () => {
      if (employmentTypeListData.length > 0) {
        return employmentTypeListData.map((item) => ({
          id: item.id,
          label: item.value || '',
          value: item.value || '',
        }))
      }

      return Object.entries(EMPLOYMENT_TYPES).map(([key, value]) => ({
        id: null,
        label: key,
        value: value,
      }))
    },
    [employmentTypeListData],
  )

  const genderOptions = useMemo(
    () => {
      if (genderListData.length > 0) {
        return genderListData.map((item) => ({
          id: item.id,
          label: item.value || '',
          value: item.id,  // store id as value for consistent lookup
        }))
      }
      return Object.entries(GENDERS).map(([key, value]) => ({
        id: null,
        label: key,
        value: value,
      }))
    },
    [genderListData],
  )

  const bloodGroupOptions = useMemo(
    () => {
      if (bloodGroupListData.length > 0) {
        return bloodGroupListData.map((item) => ({
          id: item.id,
          label: item.value || '',
          value: item.id,  // store id as value for consistent lookup
        }))
      }
      return Object.entries(BLOOD_GROUPS).map(([key, value]) => ({
        id: null,
        label: key,
        value: value,
      }))
    },
    [bloodGroupListData],
  )

  // Memoized active users (not on inactive accounts)
  const activeUsers = useMemo(() => users.filter((it) => it.is_account_active), [users])


  // Lazily fetch user_type children when category changes
  const [availableUserTypes, setAvailableUserTypes] = useState([])
  useEffect(() => {
    if (!userDetails.category) {
      setAvailableUserTypes(userTypeListData)
      return
    }
    listDataService
      .getChildrenByParent(user, userDetails.category)
      .then((children) => {
        const childIds = new Set(children.map((c) => c.id))
        const filtered = userTypeListData.filter((ut) => childIds.has(ut.id))
        setAvailableUserTypes(filtered.length > 0 ? filtered : userTypeListData)
      })
      .catch(() => setAvailableUserTypes(userTypeListData))
  }, [userDetails.category, userTypeListData, user])

  /**
   * Derive which work_status item corresponds to 'notice_period' and 'probation'
   * so we can conditionally show date fields.
   */
  const noticePeriodItem = useMemo(
    () => workStatusListData.find((it) => it.value?.toLowerCase().includes('notice')),
    [workStatusListData],
  )
  const probationItem = useMemo(
    () => workStatusListData.find((it) => it.value?.toLowerCase().includes('probation')),
    [workStatusListData],
  )

  /**
   * Determine whether the selected category is 'internal' so we can show
   * work_status (only meaningful for internal users).
   */
  const selectedCategoryIsInternal = useMemo(() => {
    const cat = categoryListData.find((it) => it.id === userDetails.category)
    return cat?.value?.toLowerCase().includes('internal') ?? false
  }, [userDetails.category, categoryListData])


  // Generic field update handler
  const handleFieldChange = useCallback((field, value) => {
    setUserDetails((prev) => {
      const updated = { ...prev, [field]: value }
      return updated
    })
  }, [])

  // Handle user selection
  const handleUserSelect = useCallback((selectedUser) => {
    console.log('[AddEditUserDialog] handleUserSelect called with:', selectedUser)
    if (selectedUser) {
      const mergedDetails = {
        ...defaultValue,
        ...selectedUser,
        password: '',
      }
      console.log('[AddEditUserDialog] Merged userDetails after selection:', mergedDetails)
      console.log('[AddEditUserDialog] Key fields from selected user:', {
        id: selectedUser.id,
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        email: selectedUser.email,
        department: selectedUser.department,
        designation: selectedUser.designation,
        sub_department: selectedUser.sub_department,
        reporting_manager_id: selectedUser.reporting_manager_id,
        is_admin: selectedUser.is_admin,
        is_account_active: selectedUser.is_account_active,
      })
      setSelectedUser(selectedUser)
      setUserDetails(mergedDetails)
    } else {
      console.log('[AddEditUserDialog] User deselected, resetting to defaults')
      setSelectedUser(null)
      setUserDetails(defaultValue)
      setReplacementUser(null)
    }
  }, [])

  // Handle dialog close with cleanup
  const handleClose = useCallback(() => {
    setOpenClose(false)
    setSelectedUser(null)
    setUserDetails(defaultValue)
    setIsSubmitting(false)
    setReplacementNeeded(null)
    setReplacementUser(null)
  }, [setOpenClose])

  // Validation
  const isFormValid = useMemo(() => {
    const baseValid =
      userDetails.first_name?.trim() &&
      userDetails.last_name?.trim() &&
      userDetails.email?.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email) &&
      userDetails.category != null &&
      userDetails.user_type != null

    // notice_period_end_date required when work_status = notice period
    if (noticePeriodItem && userDetails.work_status === noticePeriodItem.id) {
      return baseValid && !!userDetails.notice_period_end_date
    }

    return baseValid
  }, [
    userDetails.first_name,
    userDetails.last_name,
    userDetails.email,
    userDetails.category,
    userDetails.user_type,
    userDetails.work_status,
    userDetails.notice_period_end_date,
    noticePeriodItem,
  ])

  // Submit handler
  const addEditUser = async () => {
    if (!isFormValid) {
      toast.error('Please fill in all required fields correctly', TOAST_SETTINGS_ERROR)
      return
    }

    if (userDetails.is_account_active === false) {
      if (!replacementNeeded) {
        toast.error('Please select if a replacement user is needed.', TOAST_SETTINGS_ERROR)
        return
      }
      if (replacementNeeded === 'yes' && !replacementUser) {
        toast.error('Please select a replacement user.', TOAST_SETTINGS_ERROR)
        return
      }
    }

    setIsSubmitting(true)

    const payload = {
      ...userDetails,
      company_id: selectedUser?.company_id || user.company_id,
      password: '',
      user_id: selectedUser?.id || -1,
      replacement_user_id: replacementNeeded === 'yes' ? replacementUser?.id || null : null,
    }

    try {
      await adminTeamsService.addEditUser(user, payload)
      toast.success('Saved successfully!', TOAST_SETTINGS)
      handleClose()
      fetchFreshList()
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong!', TOAST_SETTINGS_ERROR)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Helper to format user label
  const getUserLabel = (user) => (user ? formatUserDisplayName(user) : '')

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{selectedUser ? 'Edit User' : 'Create User'}</Typography>
          <IconButton onClick={handleClose} size="small">
            <HighlightOffIcon />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent>
        {/* User Selection */}
        <Autocomplete
          value={selectedUser}
          size="small"
          options={users}
          getOptionLabel={getUserLabel}
          renderInput={(params) => <TextField {...params} label="Select User To Edit" />}
          onChange={(_, value) => handleUserSelect(value)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />

        {/* Classification Fields */}
        <AutocompleteField
          label="Category"
          options={categoryListData}
          value={categoryListData.find((item) => item.id === userDetails.category) || null}
          getOptionLabel={(option) => option.value || ''}
          onChange={(_, value) => {
            handleFieldChange('category', value?.id || null)
            // Reset user_type when category changes
            handleFieldChange('user_type', null)
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          required
        />

        <AutocompleteField
          label="User Type"
          options={availableUserTypes}
          value={availableUserTypes.find((item) => item.id === userDetails.user_type) || null}
          getOptionLabel={(option) => option.value || ''}
          onChange={(_, value) => handleFieldChange('user_type', value?.id || null)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          required
          disabled={!userDetails.category}
        />

        {/* Basic Information */}
        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          required
          color="primary"
          variant="outlined"
          label="First Name"
          value={userDetails.first_name || ''}
          onChange={(e) => handleFieldChange('first_name', e.target.value)}
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="Middle Name (Optional)"
          value={userDetails.middle_name || ''}
          onChange={(e) => handleFieldChange('middle_name', e.target.value)}
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          required
          color="primary"
          variant="outlined"
          label="Last Name"
          value={userDetails.last_name || ''}
          onChange={(e) => handleFieldChange('last_name', e.target.value)}
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          type="email"
          fullWidth
          required
          color="primary"
          variant="outlined"
          label="Email"
          value={userDetails.email || ''}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          size="small"
          error={userDetails.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)}
          helperText={
            userDetails.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)
              ? 'Invalid email format'
              : ''
          }
        />

        {selectedCategoryIsInternal && (
          <>
            {/* Reporting Manager */}
            <AutocompleteField
              label="Reporting Manager"
              options={activeUsers}
              value={activeUsers.find((it) => it.id === userDetails.reporting_manager_id) || null}
              getOptionLabel={getUserLabel}
              onChange={(_, value) => handleFieldChange('reporting_manager_id', value?.id || null)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {/* Employee ID */}
            <TextField
              style={{ marginTop: 16 }}
              fullWidth
              color="primary"
              variant="outlined"
              label="Employee ID"
              value={userDetails.employee_id || ''}
              onChange={(e) => handleFieldChange('employee_id', e.target.value)}
              size="small"
            />

            {/* Clean Schema List Data Fields */}
            <AutocompleteField
              label="Designation"
              options={designationListData}
              value={
                designationListData.find((item) => item.id === userDetails.designation) ||
                null
              }
              getOptionLabel={(option) => option.value || ''}
              onChange={(_, value) => handleFieldChange('designation', value?.id || null)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <AutocompleteField
              label="Department"
              options={departmentListData}
              value={
                departmentListData.find((item) => item.id === userDetails.department) ||
                null
              }
              getOptionLabel={(option) => option.value || ''}
              onChange={(_, value) => handleFieldChange('department', value?.id || null)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <AutocompleteField
              label="Sub-function"
              options={subfunctionListData}
              value={
                subfunctionListData.find((item) => item.id === userDetails.sub_department) ||
                null
              }
              getOptionLabel={(option) => option.value || ''}
              onChange={(_, value) => handleFieldChange('sub_department', value?.id || null)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </>)}
        {selectedCategoryIsInternal && (
          <>
            {/* Work Status — shown for all but most relevant for internal users */}
            <AutocompleteField
              label="Work Status"
              options={workStatusListData}
              value={workStatusListData.find((item) => item.id === userDetails.work_status) || null}
              getOptionLabel={(option) => option.value || ''}
              onChange={(_, value) => {
                handleFieldChange('work_status', value?.id || null)
                // Clear date fields when work_status changes
                if (!noticePeriodItem || value?.id !== noticePeriodItem.id) {
                  handleFieldChange('notice_period_end_date', null)
                }
                if (!probationItem || value?.id !== probationItem.id) {
                  handleFieldChange('probation_end_date', null)
                }
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {/* Notice Period End Date: required when work_status = notice period */}
            {noticePeriodItem && userDetails.work_status === noticePeriodItem.id && (
              <DateAndDateTime
                type={FIELD_TYPES.DATE}
                style={{ marginTop: 16 }}
                label="Notice Period End Date *"
                value={
                  userDetails.notice_period_end_date
                    ? moment(userDetails.notice_period_end_date)
                    : null
                }
                onChange={(date) =>
                  handleFieldChange(
                    'notice_period_end_date',
                    date ? date.format('YYYY-MM-DD') : null,
                  )
                }
              />
            )}

            {/* Probation End Date: shown for internal full-time/part-time users */}
            {probationItem && userDetails.work_status === probationItem.id && (
              <DateAndDateTime
                type={FIELD_TYPES.DATE}
                style={{ marginTop: 16 }}
                label="Probation End Date"
                value={
                  userDetails.probation_end_date ? moment(userDetails.probation_end_date) : null
                }
                onChange={(date) =>
                  handleFieldChange(
                    'probation_end_date',
                    date ? date.format('YYYY-MM-DD') : null,
                  )
                }
              />
            )}
          </>
        )}



        {!selectedCategoryIsInternal && (
          <Box style={{ marginTop: 16 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowOptionalExternalFields((prev) => !prev)}
            >
              {showOptionalExternalFields ? '- Hide Additional Information' : '+ Add Additional Information'}
            </Button>
          </Box>
        )}

        {(selectedCategoryIsInternal || showOptionalExternalFields) && (
          <>
            {/* Contact Information */}
            <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="Primary Contact"
          value={userDetails.primary_contact || ''}
          onChange={(e) => handleFieldChange('primary_contact', e.target.value)}
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="Office Contact"
          value={userDetails.office_contact || ''}
          onChange={(e) => handleFieldChange('office_contact', e.target.value)}
          size="small"
        />

        {/* Dates */}
        <DateAndDateTime
          type={FIELD_TYPES.DATE}
          style={{ marginTop: 16 }}
          label="Date Of Joining"
          value={userDetails.date_of_joining ? moment(userDetails.date_of_joining) : null}
          onChange={(date) =>
            handleFieldChange('date_of_joining', date ? date.format('YYYY-MM-DD') : null)
          }
        />

        <DateAndDateTime
          type={FIELD_TYPES.DATE}
          style={{ marginTop: 16 }}
          label="Date Of Birth"
          value={userDetails.date_of_birth ? moment(userDetails.date_of_birth) : null}
          onChange={(date) =>
            handleFieldChange('date_of_birth', date ? date.format('YYYY-MM-DD') : null)
          }
        />

        {/* Gender */}
        <AutocompleteField
          label="Gender"
          options={genderOptions}
          value={genderOptions.find((item) => item.value === userDetails.gender) || null}
          getOptionLabel={(option) => option.label || ''}
          onChange={(_, value) => handleFieldChange('gender', value?.value || null)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
        />

        {/* Blood Group */}
        <AutocompleteField
          label="Blood Group"
          options={bloodGroupOptions}
          value={bloodGroupOptions.find((item) => item.value === userDetails.blood_group) || null}
          getOptionLabel={(option) => option.label || ''}
          onChange={(_, value) => handleFieldChange('blood_group', value?.value || null)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
        />

        {/* Addresses */}
        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="Permanent Address"
          value={userDetails.permanent_address || ''}
          onChange={(e) => handleFieldChange('permanent_address', e.target.value)}
          size="small"
          multiline
          minRows={2}
        />

        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="Temporary Address"
          value={userDetails.temporary_address || ''}
          onChange={(e) => handleFieldChange('temporary_address', e.target.value)}
          size="small"
          multiline
          minRows={2}
        />

        {/* Date of Leaving */}
        <DateAndDateTime
          type={FIELD_TYPES.DATE}
          style={{ marginTop: 16 }}
          label="Date Of Leaving"
          value={userDetails.date_of_leaving ? moment(userDetails.date_of_leaving) : null}
          onChange={(date) =>
            handleFieldChange('date_of_leaving', date ? date.format('YYYY-MM-DD') : null)
          }
        />

        {/* Identification Numbers */}
        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="Aadhar Number"
          value={userDetails.aadhar_number || ''}
          onChange={(e) => handleFieldChange('aadhar_number', e.target.value)}
          size="small"
        />

        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="PAN Number"
          value={userDetails.pan_number || ''}
          onChange={(e) => handleFieldChange('pan_number', e.target.value)}
          size="small"
        />

        {/* Short Bio */}
        <TextField
          style={{ marginTop: 16 }}
          fullWidth
          color="primary"
          variant="outlined"
          label="Short Bio"
          value={userDetails.short_bio || ''}
          onChange={(e) => handleFieldChange('short_bio', e.target.value)}
          size="small"
          multiline
          minRows={3}
        />
        </>
      )}

        {userDetails.is_account_active === false && (
          <AutocompleteField
            label="Is a replacement user needed?"
            options={yesNoOptions}
            value={replacementNeeded || null}
            getOptionLabel={(option) => option}
            onChange={(_, newValue) => {
              setReplacementNeeded(newValue?.toLowerCase() || null)
              setReplacementUser(null)
            }}
            isOptionEqualToValue={(option, value) => option === value}
          />
        )}

        {replacementNeeded === 'yes' && (
          <div style={{ marginTop: '16px' }}>
            <Autocomplete
              value={replacementUser}
              options={activeUsers.filter((u) => u.id !== selectedUser?.id)}
              getOptionLabel={getUserLabel}
              renderInput={(params) => (
                <TextField {...params} label="Select User for Replacement" />
              )}
              onChange={(_, value) => setReplacementUser(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {replacementUser && (
              <>
                <div style={{ marginTop: '16px' }}>
                  <strong>Current User (To be Terminated):</strong>
                  <div>{selectedUser ? formatUserDisplayName(selectedUser) : '-'}</div>
                </div>

                <div>
                  <strong>Replacement User:</strong>
                  <div>
                    {formatUserDisplayName(replacementUser)}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {replacementNeeded === 'no' && (
          <>
            <div style={{ marginTop: '16px' }}>
              The user will be terminated with no replacement. All their reporting relationships
              will be removed.
            </div>
          </>
        )}

        {/* Account Status Card */}
        <Box
          style={{
            marginTop: 16,
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            padding: '10px 14px',
          }}
        >
          <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
            ACCOUNT STATUS
          </Typography>

          <Box style={{ display: 'flex', gap: 16, marginTop: 8, alignItems: 'center' }}>
            <FormControlLabel
              style={{ flexShrink: 0 }}
              control={
                <Checkbox
                  checked={userDetails.is_admin || false}
                  color="primary"
                  onChange={(e) => handleFieldChange('is_admin', e.target.checked)}
                />
              }
              label={<Typography variant="body2">Admin</Typography>}
            />

            <FormControlLabel
              style={{ flexShrink: 0 }}
              control={
                <Checkbox
                  checked={userDetails.is_account_active !== false}
                  color="primary"
                  onChange={(e) => {
                    handleFieldChange('is_account_active', e.target.checked)
                    if (!e.target.checked) {
                      setReplacementNeeded(null)
                      setReplacementUser(null)
                    }
                  }}
                />
              }
              label={<Typography variant="body2">Account Active</Typography>}
            />
          </Box>
        </Box>

        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button
            color="primary"
            variant="contained"
            type="button"
            size="small"
            onClick={addEditUser}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : selectedUser ? 'Update User Details' : 'Create User'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default AddEditUserDialog
